import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import GeniLoader from "../components/GeniLoader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useLanguage } from "../context/LanguageContext";
import { toast } from "sonner";
import {
  ArrowsClockwise,
  CheckCircle,
  XCircle,
  Key,
  Trash,
  Info,
  Calendar,
  Shield,
  CurrencyDollar,
  Eye,
  EyeSlash,
  PlusCircle,
  ChatCenteredText,
  Copy,
  WarningCircle,
  Plug,
  ChartBar,
  Lightning
} from "phosphor-react";

const LBANK_GENI_IP = "138.199.227.58";

// Official exchange brand icons (stored locally under public/assets/exchanges)
const EXCHANGE_LOGOS = {
  binance: "/assets/exchanges/binance.png",
  bitget: "/assets/exchanges/bitget.png",
  lbank: "/assets/exchanges/lbank.png",
  mexc: "/assets/exchanges/mexc.png",
  bingx: "/assets/exchanges/bingx.png",
};

const ExchangeLogo = ({ exchange, className = "w-5 h-5" }) => {
  const src = EXCHANGE_LOGOS[exchange];
  if (!src) return null;
  return (
    <img
      src={src}
      alt={`${exchange} logo`}
      className={`${className} rounded-sm object-contain flex-shrink-0`}
      onError={(e) => { e.currentTarget.style.display = "none"; }}
      data-testid={`exchange-logo-${exchange}`}
    />
  );
};

// Trade Style -> timeframe interno já existente (Analytics inalterado). Ordem para os selects.
const TRADE_STYLES = [
  { value: "5M", label: "Scalp" },
  { value: "1H", label: "Day Trade" },
  { value: "4H", label: "Swing" },
  { value: "1D", label: "Position" },
];
const TF_TO_STYLE_LABEL = { "5M": "Scalp", "1H": "Day Trade", "4H": "Swing", "1D": "Position" };
const styleLabelForTf = (tf) => TF_TO_STYLE_LABEL[tf] || tf;

// Espelha EXATAMENTE a regra do backend (recognition_service.infer_timeframe_from_duration):
// <=4h -> 5M (Scalp) | >4h e <=30h -> 1H (Day Trade) | >30h -> 4H (Swing). null se datas inválidas/closed<opened.
const inferTimeframeFromDuration = (openedAt, closedAt) => {
  if (!openedAt || !closedAt) return null;
  const o = new Date(openedAt).getTime();
  const c = new Date(closedAt).getTime();
  if (Number.isNaN(o) || Number.isNaN(c)) return null;
  const durationSec = (c - o) / 1000;
  if (durationSec < 0) return null;
  if (durationSec <= 4 * 3600) return "5M";
  if (durationSec <= 30 * 3600) return "1H";
  return "4H";
};

export default function ExchangeTrades({ mode = "connections" }) {
  const { language } = useLanguage();
  
  // Connection Form State
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [copiedIp, setCopiedIp] = useState(false);
  
  // Exchange Selection & Request States
  const [selectedExchange, setSelectedExchange] = useState(mode === "results" ? null : "binance");
  const [activeConnections, setActiveConnections] = useState(null); // null = não carregado ainda
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestedExchangeName, setRequestedExchangeName] = useState("");
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // App / API Loading State
  const [connection, setConnection] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [apiTrades, setApiTrades] = useState([]);
  const [recognizing, setRecognizing] = useState(false);
  const [completeTrade, setCompleteTrade] = useState(null);
  const [setups, setSetups] = useState([]);
  const [cSetup, setCSetup] = useState("");
  const [cTimeframe, setCTimeframe] = useState("");
  const [cHandSize, setCHandSize] = useState("");
  const [completing, setCompleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Bulk completion (fila de trades pendentes)
  const [selectedIds, setSelectedIds] = useState([]);           // ids de trades selecionados
  const [draftValues, setDraftValues] = useState({});           // { [id]: {setup_id, timeframe, hand_size} } (edição temporária)
  const [bulkSetup, setBulkSetup] = useState("");
  const [bulkTimeframe, setBulkTimeframe] = useState("");
  const [bulkHandSize, setBulkHandSize] = useState("");
  const [bulkCompleting, setBulkCompleting] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);
  const [showAutoFillModal, setShowAutoFillModal] = useState(false);
  const [autoFillSetup, setAutoFillSetup] = useState("");
  const [estimatedHandSize, setEstimatedHandSize] = useState(null); // populado apenas pelo clique em Auto Fill

  // Internationalization fallback dictionary
  const dict = {
    ptBR: {
      title: "API TRADES",
      pendingTitle: "PENDING TRADES",
      subtitle: "Conecte suas corretoras e importe automaticamente suas operações executadas.",
      resultsSubtitle: "Fila central de trades a completar (API + Import, todas as corretoras).",
      resultsNoConnectionTitle: "Nenhuma corretora conectada",
      resultsNoConnectionDesc: "Conecte uma corretora no menu API para começar a importar e visualizar seus resultados aqui.",
      resultsGoConnect: "Conectar corretora",
      connected: "Conectado",
      notConnected: "Não Conectado",
      apiKey: "Chave de API",
      apiSecret: "Segredo de API (Secret Key)",
      passphraseLabel: "Passphrase da API",
      testBtn: "Testar Conexão",
      saveBtn: "Salvar Conexão",
      syncBtn: "Sincronizar Agora",
      disconnectBtn: "Desconectar Corretora",
      syncing: "Sincronizando...",
      testing: "Testando...",
      saving: "Salvando...",
      executionsTitle: "Execuções Recentes",
      resultsTableTitle: "Resultados",
      assetCol: "Ativo",
      resultCol: "Resultado",
      valueCol: "Valor",
      resultProfit: "Lucro",
      resultLoss: "Prejuízo",
      resultZero: "Zero",
      noResults: "Nenhum resultado realizado encontrado ainda. Clique em 'Sincronizar Agora'.",
      noExecutions: "Nenhuma execução importada ainda. Clique em 'Sincronizar Agora' acima.",
      dateCol: "Data / Hora",
      symbolCol: "Símbolo",
      sideCol: "Direção",
      qtyCol: "Quantidade",
      priceCol: "Preço",
      feeCol: "Taxa (Fee)",
      pnlCol: "P&L Realizado",
      placeholderKey: "Insira sua API Key do LBank Futures",
      placeholderSecret: "Insira seu API Secret do LBank Futures",
      placeholderKeyBinance: "Insira sua API Key da Binance Futures",
      placeholderSecretBinance: "Insira seu API Secret da Binance Futures",
      placeholderKeyBitget: "Insira sua API Key do Bitget Futures",
      placeholderSecretBitget: "Insira seu API Secret do Bitget Futures",
      placeholderPassphraseBitget: "Insira sua Passphrase do Bitget Futures",
      placeholderKeyMexc: "Insira sua API Key da MEXC Futures",
      placeholderSecretMexc: "Insira seu API Secret da MEXC Futures",
      placeholderKeyBingx: "Insira sua API Key da BingX Futures",
      placeholderSecretBingx: "Insira seu API Secret da BingX Futures",
      buy: "COMPRA / LONG",
      sell: "VENDA / SHORT",
      disconnectConfirm: "Tem certeza que deseja desconectar a corretora? Todas as execuções importadas serão apagadas.",
      testSuccess: "Conexão estabelecida com sucesso!",
      testFailed: "Falha de conexão: verifique suas credenciais.",
      saveSuccess: "Conexão com exchange salva com sucesso!",
      syncSuccess: "Sincronização concluída com sucesso!",
      disconnectSuccess: "Corretora desconectada e dados limpos.",
      lbankDesc: "Integração via API somente leitura (Read-Only) para LBank Futures/Perpetual.",
      binanceDesc: "Integração via API somente leitura (Read-Only) para Binance USDⓈ-M Futures.",
      bitgetDesc: "Integração via API somente leitura (Read-Only) para Bitget USDT-M Futures.",
      mexcDesc: "Integração via API somente leitura (Read-Only) para MEXC Futures/Perpetual.",
      bingxDesc: "Integração via API somente leitura (Read-Only) para BingX USDT-M Perpetual Futures.",
      
      // Multi-exchange features
      exchangeLabel: "Selecione a Corretora",
      connectCardTitle: "CONECTAR CORRETORA",
      comingSoonTitle: "Integração em breve",
      comingSoonDesc: "Esta corretora está sendo preparada para o API Trades.",
      requestExchangeLink: "Não encontrou sua corretora? Solicitar integração",
      requestModalTitle: "Qual corretora você gostaria de conectar?",
      requestNamePlaceholder: "Ex: Binance Spot, Bitget Futures, MEXC, etc.",
      submitRequestBtn: "Enviar Solicitação",
      submitRequestSuccess: "Solicitação enviada com sucesso! Obrigado pelo feedback.",
      submitRequestFailed: "Erro ao enviar solicitação. Tente novamente.",
      securityTitle: "Segurança de Dados",
      securityNote: "As credenciais de API são criptografadas no servidor e nunca expostas no navegador.",
      securityItem1: "Chave restrita de Apenas Leitura (Read-Only).",
      securityItem2: "Sem capacidade de saque ou transferências.",
      securityItem3: "Credenciais criptografadas no servidor.",
      activeConnectionsTitle: "Suas Conexões Ativas",
      availableStatus: "Disponível",
      comingSoonStatus: "Em Breve",
      // LBank API configuration guide
      lbankGuideTitle: "Como configurar sua API LBank",
      lbankGuideOpen: "Como conectar a LBank?",
      lbankGuideIntro: "Siga os passos abaixo para gerar sua API na LBank com as permissões corretas:",
      lbankStep1: "Na LBank, acesse My API e crie uma nova API.",
      lbankStep2: "Em Verification Method, selecione HmacSHA256.",
      lbankStep3: "Em IP Address, adicione exatamente o IP abaixo:",
      lbankStep4: "Em Authority, habilite SOMENTE Read only e Futures.",
      lbankStep5: "Copie a API Key e a Secret Key para o GENI e faça a conexão.",
      lbankImageAlt: "Exemplo de configuração correta da API LBank",
      lbankIpLabel: "Endereço IP",
      lbankCopy: "Copiar",
      lbankCopied: "Copiado!",
      lbankRequiredPerms: "Permissões necessárias",
      lbankNotRequired: "Não é necessário",
      lbankReadOnly: "Read only",
      lbankFutures: "Futures",
      lbankReadOnlyWarning: "Read Only sozinho NÃO é suficiente para acessar os dados de Futures. Read Only + Futures precisam estar habilitados. Isso NÃO significa que o GENI pode executar trades — a permissão Trade permanece desabilitada.",
      lbankSecurityInfo: "O GENI usa acesso somente leitura para obter os dados da sua conta de Futures. Permissões de negociação e saque não são necessárias.",
      lbankPermError: "Permissão da API LBank ausente. Confirme que Read Only e Futures estão habilitados",
      lbankIpReminder: "e que o IP 138.199.227.58 está adicionado na sua API."
    },
    en: {
      title: "API TRADES",
      pendingTitle: "PENDING TRADES",
      subtitle: "Connect your exchanges and automatically import your executed trades.",
      resultsSubtitle: "Central queue of trades to complete (API + Import, all exchanges).",
      resultsNoConnectionTitle: "No exchange connected",
      resultsNoConnectionDesc: "Connect an exchange from the API menu to start importing and viewing your results here.",
      resultsGoConnect: "Connect exchange",
      connected: "Connected",
      notConnected: "Not Connected",
      apiKey: "API Key",
      apiSecret: "API Secret Key",
      passphraseLabel: "API Passphrase",
      testBtn: "Test Connection",
      saveBtn: "Save Connection",
      syncBtn: "Sync Now",
      disconnectBtn: "Disconnect Exchange",
      syncing: "Syncing...",
      testing: "Testing...",
      saving: "Saving...",
      executionsTitle: "Recent Executions",
      resultsTableTitle: "Results",
      assetCol: "Asset",
      resultCol: "Result",
      valueCol: "Value",
      resultProfit: "Profit",
      resultLoss: "Loss",
      resultZero: "Zero",
      noResults: "No realized results found yet. Click 'Sync Now'.",
      noExecutions: "No executions imported yet. Click 'Sync Now' above.",
      dateCol: "Date / Time",
      symbolCol: "Symbol",
      sideCol: "Side",
      qtyCol: "Quantity",
      priceCol: "Price",
      feeCol: "Fee",
      pnlCol: "Realized P&L",
      placeholderKey: "Enter your LBank Futures API Key",
      placeholderSecret: "Enter your LBank Futures API Secret",
      placeholderKeyBinance: "Enter your Binance Futures API Key",
      placeholderSecretBinance: "Enter your Binance Futures API Secret",
      placeholderKeyBitget: "Enter your Bitget Futures API Key",
      placeholderSecretBitget: "Enter your Bitget Futures API Secret",
      placeholderPassphraseBitget: "Enter your Bitget Futures API Passphrase",
      placeholderKeyMexc: "Enter your MEXC Futures API Key",
      placeholderSecretMexc: "Enter your MEXC Futures API Secret",
      placeholderKeyBingx: "Enter your BingX Futures API Key",
      placeholderSecretBingx: "Enter your BingX Futures API Secret",
      buy: "BUY / LONG",
      sell: "SELL / SHORT",
      disconnectConfirm: "Are you sure you want to disconnect? All imported executions will be deleted.",
      testSuccess: "Connection established successfully!",
      testFailed: "Connection failed: check your credentials.",
      saveSuccess: "Exchange connection saved successfully!",
      syncSuccess: "Synchronization completed successfully!",
      disconnectSuccess: "Exchange disconnected and records cleared.",
      lbankDesc: "Read-only API integration for LBank Futures/Perpetual contracts.",
      binanceDesc: "Read-only API integration for Binance USDⓈ-M Futures.",
      bitgetDesc: "Read-only API integration for Bitget USDT-M Futures.",
      mexcDesc: "Read-only API integration for MEXC Futures/Perpetual contracts.",
      bingxDesc: "Read-only API integration for BingX USDT-M Perpetual Futures.",
      
      // Multi-exchange features
      exchangeLabel: "Select Exchange",
      connectCardTitle: "CONNECT EXCHANGE",
      comingSoonTitle: "Integration coming soon",
      comingSoonDesc: "This exchange is being prepared for API Trades.",
      requestExchangeLink: "Can't find your exchange? Request integration",
      requestModalTitle: "Which exchange would you like to connect?",
      requestNamePlaceholder: "E.g., Binance Spot, Bitget Futures, MEXC, etc.",
      submitRequestBtn: "Submit Request",
      submitRequestSuccess: "Request submitted successfully! Thank you for your feedback.",
      submitRequestFailed: "Failed to submit request. Please try again.",
      securityTitle: "Data Security",
      securityNote: "API credentials are encrypted server-side and never exposed in the browser.",
      securityItem1: "Read-only API permissions.",
      securityItem2: "No withdrawal capability.",
      securityItem3: "Credentials encrypted server-side.",
      activeConnectionsTitle: "Your Active Connections",
      availableStatus: "Available",
      comingSoonStatus: "Coming Soon",
      // LBank API configuration guide
      lbankGuideTitle: "How to configure your LBank API",
      lbankGuideOpen: "How to connect LBank?",
      lbankGuideIntro: "Follow the steps below to create your LBank API with the correct permissions:",
      lbankStep1: "On LBank, go to My API and create a new API.",
      lbankStep2: "Under Verification Method, select HmacSHA256.",
      lbankStep3: "Under IP Address, add exactly the IP below:",
      lbankStep4: "Under Authority, enable ONLY Read only and Futures.",
      lbankStep5: "Copy the API Key and Secret Key into GENI and connect.",
      lbankImageAlt: "Example of the correct LBank API configuration",
      lbankIpLabel: "IP Address",
      lbankCopy: "Copy",
      lbankCopied: "Copied!",
      lbankRequiredPerms: "Required permissions",
      lbankNotRequired: "Not required",
      lbankReadOnly: "Read only",
      lbankFutures: "Futures",
      lbankReadOnlyWarning: "Read Only alone is NOT enough to access Futures data. Read Only + Futures must both be enabled. This does NOT mean GENI can execute trades — the Trade permission stays disabled.",
      lbankSecurityInfo: "GENI uses read-only access to retrieve your Futures account data. Trading and withdrawal permissions are not required.",
      lbankPermError: "LBank API permission missing. Make sure Read Only and Futures are enabled",
      lbankIpReminder: "and IP 138.199.227.58 is added to your API."
    }
  };

  const t = dict[language === "ptBR" ? "ptBR" : "en"];

  // Detect LBank "No permission" (error_code 10009) and return a specific, actionable message
  const lbankPermissionHint = (rawMsg) => {
    if (selectedExchange !== "lbank") return null;
    const s = String(rawMsg || "").toLowerCase();
    if (s.includes("10009") || s.includes("no permission")) {
      return `${t.lbankPermError} ${t.lbankIpReminder}`;
    }
    return null;
  };

  const handleCopyIp = async () => {
    try {
      await navigator.clipboard.writeText(LBANK_GENI_IP);
      setCopiedIp(true);
      setTimeout(() => setCopiedIp(false), 2000);
    } catch (e) {
      toast.error(language === "ptBR" ? "Não foi possível copiar o IP." : "Could not copy IP.");
    }
  };

  // Load connection and executions whenever selected exchange changes (connections mode only).
  // Pending Trades (mode=results) loads via its own isolated useEffect — never needs fetchData.
  useEffect(() => {
    if (mode === "results") return;
    fetchData();
  }, [selectedExchange]);

  // Fila GLOBAL de Pending Trades (API + Import, todas as exchanges). Independe de conexão.
  // Não realiza Auto Fill aqui — sem seedar draftValues automaticamente no mount/render.
  const fetchPendingTrades = async () => {
    try {
      const recRes = await api.get(`/exchange-trades/recognized-trades`);
      setApiTrades(recRes.data || []);
    } catch (e) { setApiTrades([]); }
    try {
      const s = await api.get(`/setups`);
      setSetups(Array.isArray(s.data) ? s.data : (s.data?.setups || []));
    } catch (e) { /* mantém setups atuais */ }
  };

  // Auto Fill — passo 1: abre o modal de seleção de Setup (sem request ainda).
  const openAutoFillModal = () => {
    if (selectedIds.length === 0) {
      toast.error(language === "ptBR" ? "Selecione ao menos 1 trade." : "Select at least 1 trade.");
      return;
    }
    setAutoFillSetup("");
    setShowAutoFillModal(true);
  };

  // Auto Fill — passo 2: executado somente após o usuário confirmar o Setup.
  // 1 único request (estimated-hand-size) + inferência local de Trade Style + 1 único setDraftValues.
  // Não grava no banco. Não completa trades.
  const executeAutoFill = async () => {
    if (!autoFillSetup) {
      toast.error(language === "ptBR" ? "Selecione um Setup válido." : "Select a valid Setup.");
      return;
    }
    setShowAutoFillModal(false);
    setAutoFilling(true);
    try {
      // 1. Estimated Hand Size: 1 único request, independente da quantidade de selecionados.
      let eh = null;
      try {
        const est = await api.get(`/exchange-trades/estimated-hand-size`);
        eh = est.data?.estimated_hand_size ?? null;
        setEstimatedHandSize(eh);
      } catch (_) { /* histórico insuficiente — Setup e Trade Style serão preenchidos sem Hand Size */ }

      // 2. Monta todos os drafts em memória → 1 único setDraftValues (sem loop de setState por trade).
      const selected = apiTrades.filter(
        (tr) => tr.completion_status === "pending_completion" && selectedIds.includes(tr.id)
      );
      setDraftValues((prev) => {
        const next = { ...prev };
        selected.forEach((tr) => {
          const tf = inferTimeframeFromDuration(tr.opened_at, tr.closed_at) || tr.timeframe || null;
          next[tr.id] = {
            ...(next[tr.id] || {}),
            setup_id: autoFillSetup,
            ...(tf ? { timeframe: tf } : {}),
            ...(eh != null ? { hand_size: eh } : {}),
          };
        });
        return next;
      });

      toast.success(language === "ptBR"
        ? `Auto Fill aplicado a ${selectedIds.length} trade(s).`
        : `Auto Fill applied to ${selectedIds.length} trade(s).`);
    } catch (e) {
      toast.error(language === "ptBR" ? "Erro ao executar Auto Fill." : "Auto Fill failed.");
    } finally {
      setAutoFilling(false);
    }
  };

  // Carrega a fila global assim que a página de resultados abre (único ponto de entrada para mode=results).
  useEffect(() => {
    if (mode !== "results") return;
    fetchPendingTrades().finally(() => setLoading(false));
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const connRes = await api.get(`/exchange-trades/connection/status?exchange=${selectedExchange}`);
      setConnection(connRes.data);

      if (connRes.data) {
        const execRes = await api.get(`/exchange-trades/executions?exchange=${selectedExchange}`);
        setExecutions(execRes.data);
      } else {
        setExecutions([]);
      }
      // A lista de Pending Trades é sempre global (não filtra por exchange)
      await fetchPendingTrades();
    } catch (err) {
      console.error("Failed to load exchange trade data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!apiKey || !apiSecret || (selectedExchange === "bitget" && !passphrase)) {
      toast.error(language === "ptBR" ? "Preencha todos os campos necessários" : "Please fill in all required fields");
      return;
    }
    setTesting(true);

    try {
      const res = await api.post("/exchange-trades/connection/test", {
        exchange: selectedExchange,
        api_key: apiKey.trim(),
        api_secret: apiSecret.trim(),
        passphrase: selectedExchange === "bitget" ? passphrase.trim() : undefined
      });
      if (res.data && res.data.result) {
        toast.success(t.testSuccess);
      } else {
        const raw = res.data?.msg || "";
        const hint = lbankPermissionHint(raw);
        if (hint) {
          toast.error(hint);
        } else {
          toast.error(`${t.testFailed} (${raw || "Auth Error"})`);
        }
      }
    } catch (err) {
      const raw = err.response?.data?.detail || err.response?.data?.msg || "";
      const hint = lbankPermissionHint(raw);
      toast.error(hint || t.testFailed);
    } finally {
      setTesting(false);
    }
  };

  const handleSaveConnection = async () => {
    if (!apiKey || !apiSecret || (selectedExchange === "bitget" && !passphrase)) {
      toast.error(language === "ptBR" ? "Preencha todos os campos necessários" : "Please fill in all required fields");
      return;
    }
    setSaving(true);

    try {
      const res = await api.post("/exchange-trades/connection/save", {
        exchange: selectedExchange,
        api_key: apiKey.trim(),
        api_secret: apiSecret.trim(),
        passphrase: selectedExchange === "bitget" ? passphrase.trim() : undefined
      });
      setConnection(res.data);
      toast.success(t.saveSuccess);
      // Automatically trigger a sync on successful connection
      handleSyncNow();
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.msg || t.testFailed;
      const hint = lbankPermissionHint(errMsg);
      toast.error(hint || `${t.testFailed} (${errMsg})`);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/exchange-trades/sync?exchange=${selectedExchange}`);
      toast.success(`${t.syncSuccess} (+${res.data.new_executions_count} new executions)`);
      // Reload executions and connection metadata
      fetchData();
    } catch (err) {
      toast.error(language === "ptBR" ? "Erro ao sincronizar corretora." : "Exchange synchronization failed.");
    } finally {
      setSyncing(false);
    }
  };

  const handleRecognize = async () => {
    setRecognizing(true);
    try {
      const res = await api.post(`/exchange-trades/recognize?exchange=${selectedExchange}`);
      toast.success(`${res.data.created} ${language === "ptBR" ? "operações reconhecidas" : "trades recognized"} · ${res.data.skipped_existing} ${language === "ptBR" ? "já existentes" : "existing"}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Recognition failed");
    } finally {
      setRecognizing(false);
    }
  };

  const openCompleteModal = async (trade) => {
    setCompleteTrade(trade);
    // Prioridade: draft manual/Auto Fill > trade.timeframe > vazio. Hand Size: draft > estimatedHandSize > vazio.
    const d = draftValues[trade.id] || {};
    setCSetup(d.setup_id || "");
    setCTimeframe(d.timeframe || trade.timeframe || "");
    setCHandSize(d.hand_size ? String(d.hand_size) : (estimatedHandSize != null ? String(estimatedHandSize) : ""));
    try {
      const s = await api.get(`/setups`);
      setSetups(Array.isArray(s.data) ? s.data : (s.data?.setups || []));
    } catch (e) { setSetups([]); }
  };

  const submitCompleteTrade = async () => {
    if (!cSetup || !cTimeframe || !cHandSize || parseFloat(cHandSize) <= 0) {
      toast.error(language === "ptBR" ? "Preencha Setup, Timeframe e Hand Size." : "Fill Setup, Timeframe and Hand Size.");
      return;
    }
    setCompleting(true);
    try {
      await api.post(`/exchange-trades/trades/${completeTrade.id}/complete`, {
        setup_id: cSetup, timeframe: cTimeframe, hand_size: parseFloat(cHandSize)
      });
      toast.success(language === "ptBR" ? "Trade completado e enviado ao Analytics." : "Trade completed and sent to Analytics.");
      setCompleteTrade(null);
      fetchPendingTrades();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete trade");
    } finally {
      setCompleting(false);
    }
  };


  // ===== Bulk completion helpers =====
  const setupName = (id) => (setups.find((s) => s.id === id)?.name) || "";

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = (ids) => {
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : ids);
  };

  const applyFieldToSelected = (field, value) => {
    if (selectedIds.length === 0) {
      toast.error(language === "ptBR" ? "Selecione ao menos 1 trade." : "Select at least 1 trade.");
      return;
    }
    if (field === "hand_size") {
      if (!value || parseFloat(value) <= 0) {
        toast.error(language === "ptBR" ? "Informe um Hand Size (Risk USD) válido." : "Enter a valid Hand Size (Risk USD).");
        return;
      }
      value = parseFloat(value);
    } else if (!value) {
      toast.error(language === "ptBR" ? "Escolha um valor para aplicar." : "Choose a value to apply.");
      return;
    }
    setDraftValues((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[id] = { ...(next[id] || {}), [field]: value };
      });
      return next;
    });
    toast.success(`${language === "ptBR" ? "Aplicado a" : "Applied to"} ${selectedIds.length} ${language === "ptBR" ? "trades" : "trades"}`);
  };

  // Aplica os 3 campos (Setup, Trade Style, Hand Size) de uma vez aos selecionados. Reutiliza a mesma lógica de draft.
  const handleApplyAll = () => {
    if (selectedIds.length === 0) {
      toast.error(language === "ptBR" ? "Selecione ao menos 1 trade." : "Select at least 1 trade.");
      return;
    }
    if (!bulkSetup || !bulkTimeframe || !bulkHandSize || parseFloat(bulkHandSize) <= 0) {
      toast.error(language === "ptBR"
        ? "Preencha Setup, Trade Style e Hand Size para usar o Apply All."
        : "Fill Setup, Trade Style and Hand Size to use Apply All.");
      return;
    }
    const hs = parseFloat(bulkHandSize);
    setDraftValues((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[id] = { ...(next[id] || {}), setup_id: bulkSetup, timeframe: bulkTimeframe, hand_size: hs };
      });
      return next;
    });
    toast.success(`${language === "ptBR" ? "Aplicado a" : "Applied to"} ${selectedIds.length} ${language === "ptBR" ? "trades" : "trades"}`);
  };


  const isTradeReady = (id) => {
    const d = draftValues[id] || {};
    return !!d.setup_id && !!d.timeframe && !!d.hand_size && parseFloat(d.hand_size) > 0;
  };

  const handleCompleteSelected = async () => {
    const eligible = selectedIds.filter(isTradeReady);
    if (eligible.length === 0) {
      toast.error(language === "ptBR"
        ? "Nenhum trade selecionado com Setup, Timeframe e Hand Size preenchidos."
        : "No selected trade has Setup, Timeframe and Hand Size filled.");
      return;
    }
    const msg = language === "ptBR" ? `Concluir ${eligible.length} trades selecionados?` : `Complete ${eligible.length} selected trades?`;
    if (!window.confirm(msg)) return;

    setBulkCompleting(true);
    try {
      const items = eligible.map((id) => ({
        trade_id: id,
        setup_id: draftValues[id].setup_id,
        timeframe: draftValues[id].timeframe,
        hand_size: parseFloat(draftValues[id].hand_size),
      }));
      const res = await api.post(`/exchange-trades/trades/complete-bulk`, { items });
      const { completed = 0, failed = 0, results = [] } = res.data || {};
      if (failed > 0) {
        toast.warning(`${language === "ptBR" ? "Concluídos" : "Completed"}: ${completed} · ${language === "ptBR" ? "Falharam" : "Failed"}: ${failed}`);
      } else {
        toast.success(`${language === "ptBR" ? "Concluídos" : "Completed"}: ${completed}`);
      }
      // Remove seleção/draft apenas dos concluídos com sucesso; falhas permanecem para correção
      const succeededIds = results.filter((r) => r.success).map((r) => r.trade_id);
      setSelectedIds((prev) => prev.filter((id) => !succeededIds.includes(id)));
      setDraftValues((prev) => {
        const next = { ...prev };
        succeededIds.forEach((id) => { delete next[id]; });
        return next;
      });
      fetchPendingTrades();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "ptBR" ? "Falha na conclusão em lote." : "Bulk completion failed."));
    } finally {
      setBulkCompleting(false);
    }
  };


  const handleDisconnect = async () => {
    if (!window.confirm(t.disconnectConfirm)) return;
    try {
      await api.post(`/exchange-trades/connection/disconnect?exchange=${selectedExchange}`);
      setConnection(null);
      setExecutions([]);
      setApiKey("");
      setApiSecret("");
      setPassphrase("");
      toast.success(t.disconnectSuccess);
    } catch (err) {
      toast.error(language === "ptBR" ? "Erro ao desconectar." : "Disconnect failed.");
    }
  };

  const handleRequestExchange = async (e) => {
    e.preventDefault();
    if (!requestedExchangeName.trim()) {
      toast.error(language === "ptBR" ? "Por favor, insira o nome da corretora." : "Please enter the exchange name.");
      return;
    }
    setSubmittingRequest(true);
    try {
      await api.post("/options/feedback", {
        category: "improvement",
        message: `User requested new exchange integration on API Trades: ${requestedExchangeName.trim()}`
      });
      toast.success(t.submitRequestSuccess);
      setRequestedExchangeName("");
      setShowRequestModal(false);
    } catch (err) {
      console.error("Failed to submit exchange request:", err);
      toast.error(t.submitRequestFailed);
    } finally {
      setSubmittingRequest(false);
    }
  };

  const formatDateTime = (isoString) => {
    try {
      const d = new Date(isoString);
      return d.toLocaleString(language === "ptBR" ? "pt-BR" : "en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      });
    } catch (e) {
      return isoString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <GeniLoader size="lg" />
      </div>
    );
  }

  // A lista "Trades" é uma fila: mostra SOMENTE trades aguardando conclusão.
  const pendingTrades = apiTrades.filter((tr) => tr.completion_status === "pending_completion");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white py-12 px-4 md:px-8 w-full max-w-[1600px] mx-auto" data-testid="exchange-trades-view">
      {/* Page Header */}
      <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3 uppercase bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
            {mode === "results" ? t.pendingTitle : t.title}
          </h1>
          <p className="text-white/60 text-lg max-w-3xl">
            {mode === "results" ? t.resultsSubtitle : t.subtitle}
          </p>
        </div>

        {/* Exchange Selector */}
        <div className="flex flex-col gap-2 min-w-[260px]">

          {/* Connections mode: lista estática com todas as exchanges suportadas */}
          {mode === "connections" && (
            <>
              <label className="text-xs font-bold text-white/40 uppercase tracking-wider">{t.exchangeLabel}</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                  <ExchangeLogo exchange={selectedExchange} className="w-5 h-5" />
                </span>
                <select
                  value={selectedExchange || "binance"}
                  onChange={(e) => {
                    setSelectedExchange(e.target.value);
                    setApiKey("");
                    setApiSecret("");
                    setPassphrase("");
                  }}
                  className="w-full bg-[#0F0F0F] border border-white/10 rounded-sm pl-11 pr-4 py-3 text-sm text-white focus:outline-none focus:border-white/30 font-bold uppercase tracking-wide cursor-pointer hover:bg-white/2 transition-all"
                  data-testid="exchange-selector-dropdown"
                >
                  <option value="binance">1. BINANCE ({t.availableStatus})</option>
                  <option value="bitget">2. BITGET ({t.availableStatus})</option>
                  <option value="lbank">3. LBANK ({t.availableStatus})</option>
                  <option value="mexc">4. MEXC ({t.availableStatus})</option>
                  <option value="bingx">5. BINGX ({t.availableStatus})</option>
                </select>
              </div>
            </>
          )}

          {/* Pending Trades é fila GLOBAL: seletor/Sync/Recognize movidos para fora desta página.
              A auto-seleção interna de selectedExchange é mantida (inofensiva) e não afeta a listagem. */}

          {/* Solicitar integração: sempre visível na página de resultados */}
          <button
            type="button"
            onClick={() => setShowRequestModal(true)}
            className="text-left text-xs text-white/40 hover:text-[#00E676] transition-colors flex items-center gap-1.5 mt-1 font-semibold"
            data-testid="request-exchange-action"
          >
            <PlusCircle size={14} />
            {t.requestExchangeLink}
          </button>
        </div>
      </div>

      {/* Main Grid: Connection Setup Card and Instruction/Meta (connection/config view only) */}
      {mode === "connections" && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
        {/* Connection Setup Card */}
        <div className="lg:col-span-2 bg-[#0F0F0F] border border-white/5 rounded-sm p-8 flex flex-col justify-between relative overflow-hidden">
          {/* Subtle Accent Glow */}
          <div className={`absolute top-0 right-0 w-24 h-24 rounded-full blur-3xl opacity-10 ${(["lbank", "binance", "bitget", "mexc", "bingx"].includes(selectedExchange)) && connection ? "bg-[#00E676]" : "bg-orange-500"}`}></div>
          
          <div>
            <div className="flex items-center justify-between mb-6 border-b border-white/5 pb-5">
              <div className="flex items-center gap-3">
                <ExchangeLogo exchange={selectedExchange} className="w-7 h-7" />
                <span className="text-2xl font-black text-white uppercase tracking-wider">{selectedExchange}</span>
                {(["lbank", "binance", "bitget", "mexc", "bingx"].includes(selectedExchange)) ? (
                  <span className="text-xs font-semibold px-2 py-1 bg-[#00E676]/10 text-[#00E676] border border-[#00E676]/20 rounded uppercase">
                    {t.availableStatus}
                  </span>
                ) : (
                  <span className="text-xs font-semibold px-2 py-1 bg-white/5 text-white/40 border border-white/10 rounded uppercase">
                    {t.comingSoonStatus}
                  </span>
                )}
              </div>
              
              {/* Connection Status Badge */}
              {(["lbank", "binance", "bitget", "mexc", "bingx"].includes(selectedExchange)) ? (
                connection ? (
                  <div className="flex items-center gap-2 px-3 py-1 bg-[#00E676]/10 border border-[#00E676]/30 rounded-sm text-[#00E676] text-xs font-bold uppercase">
                    <span className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse"></span>
                    {t.connected}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 px-3 py-1 bg-orange-500/10 border border-orange-500/30 rounded-sm text-orange-400 text-xs font-bold uppercase">
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                    {t.notConnected}
                  </div>
                )
              ) : (
                <div className="flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-sm text-white/40 text-xs font-bold uppercase">
                  <span className="w-2 h-2 rounded-full bg-white/20"></span>
                  {t.comingSoonStatus}
                </div>
              )}
            </div>

            {/* Content reactive to selected exchange */}
            {(["lbank", "binance", "bitget", "mexc", "bingx"].includes(selectedExchange)) ? (
              <div>
                <p className="text-white/50 text-sm mb-6">
                  {selectedExchange === "binance" ? t.binanceDesc : selectedExchange === "bitget" ? t.bitgetDesc : selectedExchange === "mexc" ? t.mexcDesc : selectedExchange === "bingx" ? t.bingxDesc : t.lbankDesc}
                </p>

                {!connection ? (
                  /* Setup Inputs Form */
                  <div className="space-y-5">
                    {/* LBank: compact help trigger that opens the full guide in a modal (Dialog) */}
                    {selectedExchange === "lbank" && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-indigo-500/25 bg-indigo-500/[0.06] text-indigo-300 hover:bg-indigo-500/[0.12] text-sm font-semibold transition-all"
                            data-testid="lbank-guide-open-button"
                          >
                            <Info size={16} weight="duotone" />
                            {t.lbankGuideOpen}
                          </button>
                        </DialogTrigger>
                        <DialogContent
                          className="bg-[#0F0F0F] border-white/10 text-white max-w-xl w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto p-6"
                          data-testid="lbank-config-guide"
                        >
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2.5 text-white uppercase tracking-wider text-sm pr-6">
                              <Info size={20} weight="duotone" className="text-indigo-400 flex-shrink-0" />
                              {t.lbankGuideTitle}
                            </DialogTitle>
                          </DialogHeader>

                          <p className="text-xs text-white/50">{t.lbankGuideIntro}</p>

                          <ol className="space-y-2.5">
                            {[t.lbankStep1, t.lbankStep2, t.lbankStep3, t.lbankStep4, t.lbankStep5].map((step, i) => (
                              <li key={i} className="flex items-start gap-2.5 text-sm text-white/70">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-500/15 text-indigo-300 text-[11px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>

                          {/* Example image (provided asset, not recreated) */}
                          <div className="rounded-sm overflow-hidden border border-white/10 bg-[#050505]">
                            <img
                              src="/assets/lbank-api-config.png"
                              alt={t.lbankImageAlt}
                              className="w-full h-auto block"
                              data-testid="lbank-config-example-image"
                            />
                          </div>

                          {/* IP Address with copy button */}
                          <div>
                            <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-2">{t.lbankIpLabel}</label>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 bg-[#050505] border border-white/10 rounded-sm px-4 py-2.5 text-sm text-white font-mono" data-testid="lbank-ip-value">
                                {LBANK_GENI_IP}
                              </code>
                              <button
                                type="button"
                                onClick={handleCopyIp}
                                className="flex items-center gap-1.5 px-3 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 rounded-sm text-xs font-semibold transition-all"
                                data-testid="lbank-copy-ip-button"
                              >
                                {copiedIp ? <CheckCircle size={15} weight="bold" className="text-[#00E676]" /> : <Copy size={15} />}
                                {copiedIp ? t.lbankCopied : t.lbankCopy}
                              </button>
                            </div>
                          </div>

                          {/* Permissions */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="rounded-sm border border-[#00E676]/20 bg-[#00E676]/[0.04] p-3">
                              <p className="text-[11px] font-bold text-[#00E676] uppercase tracking-wider mb-2">{t.lbankRequiredPerms}</p>
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2 text-sm text-white/80">
                                  <CheckCircle size={16} weight="bold" className="text-[#00E676] flex-shrink-0" />
                                  <span>{t.lbankReadOnly}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-white/80">
                                  <CheckCircle size={16} weight="bold" className="text-[#00E676] flex-shrink-0" />
                                  <span>{t.lbankFutures}</span>
                                </div>
                              </div>
                            </div>
                            <div className="rounded-sm border border-white/10 bg-white/[0.02] p-3">
                              <p className="text-[11px] font-bold text-white/40 uppercase tracking-wider mb-2">{t.lbankNotRequired}</p>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-white/45">
                                <div className="flex items-center gap-1.5"><XCircle size={15} className="text-white/30 flex-shrink-0" /><span>Trade</span></div>
                                <div className="flex items-center gap-1.5"><XCircle size={15} className="text-white/30 flex-shrink-0" /><span>Withdraw</span></div>
                                <div className="flex items-center gap-1.5"><XCircle size={15} className="text-white/30 flex-shrink-0" /><span>Transfer</span></div>
                              </div>
                            </div>
                          </div>

                          {/* Read-only + Futures warning */}
                          <div className="flex items-start gap-2.5 rounded-sm border border-amber-500/20 bg-amber-500/[0.05] p-3">
                            <WarningCircle size={17} weight="fill" className="text-amber-400 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-200/90 leading-relaxed">{t.lbankReadOnlyWarning}</p>
                          </div>

                          {/* Security info */}
                          <div className="flex items-start gap-2.5 text-xs text-white/45 leading-relaxed">
                            <Shield size={15} weight="duotone" className="text-[#00E676] flex-shrink-0 mt-0.5" />
                            <p>{t.lbankSecurityInfo}</p>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                    <div>
                      <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-2">{t.apiKey}</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          placeholder={selectedExchange === "binance" ? t.placeholderKeyBinance : selectedExchange === "bitget" ? t.placeholderKeyBitget : selectedExchange === "mexc" ? t.placeholderKeyMexc : selectedExchange === "bingx" ? t.placeholderKeyBingx : t.placeholderKey}
                          className="w-full bg-[#050505] border border-white/10 rounded-sm px-4 py-3 text-sm text-white focus:outline-none focus:border-white/30 font-mono"
                          data-testid="exchange-api-key-input"
                        />
                        <Key size={18} className="absolute right-4 top-3.5 text-white/30" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-2">{t.apiSecret}</label>
                      <div className="relative">
                        <input
                          type={showSecret ? "text" : "password"}
                          value={apiSecret}
                          onChange={(e) => setApiSecret(e.target.value)}
                          placeholder={selectedExchange === "binance" ? t.placeholderSecretBinance : selectedExchange === "bitget" ? t.placeholderSecretBitget : selectedExchange === "mexc" ? t.placeholderSecretMexc : selectedExchange === "bingx" ? t.placeholderSecretBingx : t.placeholderSecret}
                          className="w-full bg-[#050505] border border-white/10 rounded-sm px-4 py-3 text-sm text-white focus:outline-none focus:border-white/30 font-mono pr-12"
                          data-testid="exchange-api-secret-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecret(!showSecret)}
                          className="absolute right-4 top-3.5 text-white/30 hover:text-white/60"
                        >
                          {showSecret ? <EyeSlash size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    {/* Passphrase Input (Bitget specific) */}
                    {selectedExchange === "bitget" && (
                      <div>
                        <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-2">{t.passphraseLabel}</label>
                        <div className="relative">
                          <input
                            type={showPassphrase ? "text" : "password"}
                            value={passphrase}
                            onChange={(e) => setPassphrase(e.target.value)}
                            placeholder={t.placeholderPassphraseBitget}
                            className="w-full bg-[#050505] border border-white/10 rounded-sm px-4 py-3 text-sm text-white focus:outline-none focus:border-white/30 font-mono pr-12"
                            data-testid="exchange-api-passphrase-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassphrase(!showPassphrase)}
                            className="absolute right-4 top-3.5 text-white/30 hover:text-white/60"
                          >
                            {showPassphrase ? <EyeSlash size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Connected Info State */
                  <div className="space-y-4 py-4 bg-white/2 border border-white/5 rounded-sm px-6 mb-4">
                    <div className="flex justify-between items-center py-2 border-b border-white/5">
                      <span className="text-white/40 text-sm">{t.apiKey}</span>
                      <span className="text-sm font-mono text-white/80">{connection.api_key_masked}</span>
                    </div>
                    {connection.last_sync_at && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-white/40 text-sm">{language === "ptBR" ? "Último Sync" : "Last Sync"}</span>
                        <span className="text-sm font-medium text-white/80">{formatDateTime(connection.last_sync_at)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* Coming Soon Placeholder State */
              <div className="py-12 flex flex-col items-center justify-center text-center bg-white/[0.01] border border-dashed border-white/5 rounded px-6">
                <ArrowsClockwise className="text-white/20 mb-4 animate-spin" size={40} style={{ animationDuration: '6s' }} />
                <h4 className="text-lg font-bold text-white mb-2">{t.comingSoonTitle}</h4>
                <p className="text-sm text-white/45 max-w-md">{t.comingSoonDesc}</p>
              </div>
            )}
          </div>

          {/* Action Buttons Footer */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 pt-6 border-t border-white/5">
            {(["lbank", "binance", "bitget", "mexc", "bingx"].includes(selectedExchange)) ? (
              !connection ? (
                <>
                  <button
                    type="button"
                    onClick={handleTestConnection}
                    disabled={testing || saving}
                    className="flex-1 px-5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-sm text-sm font-semibold transition-all flex items-center justify-center gap-2"
                    data-testid="exchange-test-connection-button"
                  >
                    {testing ? (
                      <>
                        <ArrowsClockwise className="animate-spin text-white/60" size={16} />
                        {t.testing}
                      </>
                    ) : (
                      <>
                        <Info size={16} />
                        {t.testBtn}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveConnection}
                    disabled={testing || saving}
                    className="flex-1 px-5 py-3 bg-[#00E676] hover:bg-[#00c867] text-black rounded-sm text-sm font-bold transition-all flex items-center justify-center gap-2"
                    data-testid="exchange-save-connection-button"
                  >
                    {saving ? (
                      <>
                        <ArrowsClockwise className="animate-spin text-black/60" size={16} />
                        {t.saving}
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} weight="bold" />
                        {t.saveBtn}
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleSyncNow}
                    disabled={syncing}
                    className="flex-1 px-5 py-3 bg-[#00E676] hover:bg-[#00c867] text-black rounded-sm text-sm font-bold transition-all flex items-center justify-center gap-2"
                    data-testid="exchange-sync-button"
                  >
                    {syncing ? (
                      <>
                        <ArrowsClockwise className="animate-spin text-black/60" size={16} />
                        {t.syncing}
                      </>
                    ) : (
                      <>
                        <ArrowsClockwise size={16} weight="bold" />
                        {t.syncBtn}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    className="px-5 py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 rounded-sm text-sm font-semibold transition-all flex items-center justify-center gap-2"
                    data-testid="exchange-disconnect-button"
                  >
                    <Trash size={16} />
                    {t.disconnectBtn}
                  </button>
                </>
              )
            ) : (
              /* Disabled button for non-connected Coming Soon exchanges */
              <button
                type="button"
                disabled
                className="w-full px-5 py-3 bg-white/5 border border-white/5 text-white/30 rounded-sm text-sm font-semibold flex items-center justify-center gap-2 cursor-not-allowed"
              >
                <PlusCircle size={16} />
                {language === "ptBR" ? "Aguardando Liberação" : "Awaiting Release"}
              </button>
            )}
          </div>

          {/* Sincronização recente/contínua: histórico antigo via Import Exchange Data */}
          {["lbank", "mexc", "binance", "bitget", "bingx"].includes(selectedExchange) && connection && (
            <div className="mt-6 pt-5 border-t border-white/5" data-testid="import-history-hint-connections">
              <Link
                to="/exchange-import"
                className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-[#00E676] transition-colors font-semibold"
                data-testid="import-history-link-connections"
              >
                <ChartBar size={14} weight="duotone" />
                {language === "ptBR" ? "Precisa de trades mais antigos? Importe o histórico da sua corretora." : "Need older trades? Import your exchange history."}
              </Link>
            </div>
          )}
        </div>

        {/* Informative Side Card: DATA SECURITY */}
        <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-8 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Shield size={28} weight="duotone" className="text-[#00E676]" />
              <h3 className="text-lg font-black text-white uppercase tracking-wider">{t.securityTitle}</h3>
            </div>
            
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              {t.securityNote}
            </p>

            <ul className="space-y-3.5 text-sm text-white/50">
              <li className="flex items-start gap-2.5">
                <CheckCircle size={18} className="text-[#00E676] flex-shrink-0 mt-0.5" />
                <span>{t.securityItem1}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle size={18} className="text-[#00E676] flex-shrink-0 mt-0.5" />
                <span>{t.securityItem2}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <CheckCircle size={18} className="text-[#00E676] flex-shrink-0 mt-0.5" />
                <span>{t.securityItem3}</span>
              </li>
            </ul>
          </div>

          <div className="mt-8 pt-6 border-t border-white/5 flex items-start gap-3 text-xs text-white/40" data-testid="exchange-history-info">
            <Calendar size={16} className="flex-shrink-0 mt-0.5" />
            <div className="flex flex-col gap-1">
              <span>{
                {
                  binance: language === "ptBR" ? "Importa até 3 meses de histórico" : "Imports up to 3 months of history",
                  bitget: language === "ptBR" ? "Importa até 90 dias de histórico" : "Imports up to 90 days of history",
                  mexc: language === "ptBR" ? "Importa até 90 dias de histórico" : "Imports up to 90 days of history",
                  bingx: language === "ptBR" ? "Importa o histórico disponível pela API" : "Imports the history available via the API",
                  lbank: language === "ptBR" ? "Sincroniza suas operações recentes e futuras" : "Syncs your recent and future trades",
                }[selectedExchange] || (language === "ptBR" ? "Importa o histórico disponível pela API" : "Imports the history available via the API")
              }</span>
              {selectedExchange === "lbank" && (
                <Link to="/exchange-import" className="text-white/30 hover:text-[#00E676] transition-colors" data-testid="exchange-history-info-secondary">
                  {language === "ptBR" ? "Precisa de trades mais antigos? Importe o histórico da sua corretora." : "Need older trades? Import your exchange history."}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Active Connections List Block (connection/config view only) */}
      {mode === "connections" && (
      <div className="mb-12 bg-[#0F0F0F] border border-white/5 rounded-sm p-8">
        <div className="flex items-center gap-2 mb-6">
          <CheckCircle size={20} weight="duotone" className="text-[#00E676]" />
          <h3 className="text-lg font-black text-white uppercase tracking-wider">{t.activeConnectionsTitle}</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Binance status card */}
          <div 
            onClick={() => setSelectedExchange("binance")}
            className={`border rounded-sm p-4 cursor-pointer transition-all ${
              selectedExchange === "binance" 
                ? "bg-[#00E676]/5 border-[#00E676]/20 hover:border-[#00E676]/40 ring-1 ring-[#00E676]/50" 
                : "bg-white/[0.01] border-white/5 hover:border-white/10"
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <span className="flex items-center gap-2 font-bold text-white tracking-wide"><ExchangeLogo exchange="binance" className="w-4 h-4" />BINANCE</span>
              {selectedExchange === "binance" && connection ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-[#00E676]/10 text-[#00E676] rounded uppercase">
                  {t.connected}
                </span>
              ) : (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-white/5 text-white/40 rounded uppercase">
                  {selectedExchange === "binance" && !connection ? t.notConnected : t.availableStatus}
                </span>
              )}
            </div>
            <p className="text-xs text-white/40">
              {selectedExchange === "binance" && connection ? connection.api_key_masked : (language === "ptBR" ? "Clique para gerenciar" : "Click to manage")}
            </p>
          </div>

          {/* Bitget status card */}
          <div 
            onClick={() => setSelectedExchange("bitget")}
            className={`border rounded-sm p-4 cursor-pointer transition-all ${
              selectedExchange === "bitget" 
                ? "bg-[#00E676]/5 border-[#00E676]/20 hover:border-[#00E676]/40 ring-1 ring-[#00E676]/50" 
                : "bg-white/[0.01] border-white/5 hover:border-white/10"
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <span className="flex items-center gap-2 font-bold text-white tracking-wide"><ExchangeLogo exchange="bitget" className="w-4 h-4" />BITGET</span>
              {selectedExchange === "bitget" && connection ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-[#00E676]/10 text-[#00E676] rounded uppercase">
                  {t.connected}
                </span>
              ) : (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-white/5 text-white/40 rounded uppercase">
                  {selectedExchange === "bitget" && !connection ? t.notConnected : t.availableStatus}
                </span>
              )}
            </div>
            <p className="text-xs text-white/40">
              {selectedExchange === "bitget" && connection ? connection.api_key_masked : (language === "ptBR" ? "Clique para gerenciar" : "Click to manage")}
            </p>
          </div>

          {/* LBank Status card */}
          <div 
            onClick={() => setSelectedExchange("lbank")}
            className={`border rounded-sm p-4 cursor-pointer transition-all ${
              selectedExchange === "lbank" 
                ? "bg-[#00E676]/5 border-[#00E676]/20 hover:border-[#00E676]/40 ring-1 ring-[#00E676]/50" 
                : "bg-white/[0.01] border-white/5 hover:border-white/10"
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <span className="flex items-center gap-2 font-bold text-white tracking-wide"><ExchangeLogo exchange="lbank" className="w-4 h-4" />LBANK</span>
              {selectedExchange === "lbank" && connection ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-[#00E676]/10 text-[#00E676] rounded uppercase">
                  {t.connected}
                </span>
              ) : (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-white/5 text-white/40 rounded uppercase">
                  {selectedExchange === "lbank" && !connection ? t.notConnected : t.availableStatus}
                </span>
              )}
            </div>
            <p className="text-xs text-white/40">
              {selectedExchange === "lbank" && connection ? connection.api_key_masked : (language === "ptBR" ? "Clique para gerenciar" : "Click to manage")}
            </p>
          </div>

          {/* MEXC coming soon card */}
          <div 
            onClick={() => setSelectedExchange("mexc")}
            className={`border rounded-sm p-4 cursor-pointer transition-all ${
              selectedExchange === "mexc" 
                ? "bg-[#00E676]/5 border-[#00E676]/20 hover:border-[#00E676]/40 ring-1 ring-[#00E676]/50" 
                : "bg-white/[0.01] border-white/5 hover:border-white/10"
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <span className="flex items-center gap-2 font-bold text-white tracking-wide"><ExchangeLogo exchange="mexc" className="w-4 h-4" />MEXC</span>
              {selectedExchange === "mexc" && connection ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-[#00E676]/10 text-[#00E676] rounded uppercase">
                  {t.connected}
                </span>
              ) : (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-white/5 text-white/40 rounded uppercase">
                  {selectedExchange === "mexc" && !connection ? t.notConnected : t.availableStatus}
                </span>
              )}
            </div>
            <p className="text-xs text-white/40">
              {selectedExchange === "mexc" && connection ? connection.api_key_masked : (language === "ptBR" ? "Clique para gerenciar" : "Click to manage")}
            </p>
          </div>

          {/* BINGX card */}
          <div 
            onClick={() => setSelectedExchange("bingx")}
            className={`border rounded-sm p-4 cursor-pointer transition-all ${
              selectedExchange === "bingx" 
                ? "bg-[#00E676]/5 border-[#00E676]/20 hover:border-[#00E676]/40 ring-1 ring-[#00E676]/50" 
                : "bg-white/[0.01] border-white/5 hover:border-white/10"
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <span className="flex items-center gap-2 font-bold text-white tracking-wide"><ExchangeLogo exchange="bingx" className="w-4 h-4" />BINGX</span>
              {selectedExchange === "bingx" && connection ? (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-[#00E676]/10 text-[#00E676] rounded uppercase">
                  {t.connected}
                </span>
              ) : (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-white/5 text-white/40 rounded uppercase">
                  {selectedExchange === "bingx" && !connection ? t.notConnected : t.availableStatus}
                </span>
              )}
            </div>
            <p className="text-xs text-white/40">
              {selectedExchange === "bingx" && connection ? connection.api_key_masked : (language === "ptBR" ? "Clique para gerenciar" : "Click to manage")}
            </p>
          </div>
        </div>
      </div>
      )}

      {/* Pending Trades: fila GLOBAL (API + Import, todas as exchanges). Independe de conexão. */}
      {mode === "results" && (
        <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-8" data-testid="recognized-trades-container">
          <div className="flex items-center justify-between mb-6 border-b border-white/5 pb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <ChartBar size={22} weight="duotone" className="text-[#00E676]" />
              <h2 className="text-xl font-black text-white uppercase tracking-wide" data-testid="trades-title">{language === "ptBR" ? "Trades" : "Trades"}</h2>
            </div>
          </div>

          {/* Import Exchange Data: importe histórico → alimenta esta mesma fila */}
          <div className="mb-6" data-testid="import-history-hint-results">
            <Link
              to="/exchange-import"
              className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-[#00E676] transition-colors font-semibold"
              data-testid="import-history-link-results"
            >
              <ChartBar size={14} weight="duotone" />
              {language === "ptBR" ? "Precisa de trades mais antigos? Importe o histórico da sua corretora." : "Need older trades? Import your exchange history."}
            </Link>
          </div>

          {pendingTrades.length === 0 ? (
            <div className="text-center py-10" data-testid="trades-empty-state">
              <CheckCircle size={40} weight="duotone" className="mx-auto text-white/20 mb-3" />
              <p className="text-white/40 text-sm">{language === "ptBR" ? "Nenhum trade aguardando conclusão." : "No trades awaiting completion."}</p>
            </div>
          ) : (
            <>
              {/* Barra de edição em lote (aparece com >=1 selecionado) */}
              {selectedIds.length > 0 && (
                <div className="mb-5 p-4 bg-[#00E676]/[0.04] border border-[#00E676]/20 rounded-sm flex flex-wrap items-end gap-3" data-testid="bulk-edit-bar">
                  <span className="text-xs font-bold text-[#00E676] uppercase tracking-wider mr-1 self-center" data-testid="bulk-selected-count">
                    {selectedIds.length} {language === "ptBR" ? "selecionados" : "selected"}
                  </span>
                  {/* Auto Fill: infere Trade Style por duração + Hand Size estimado (1 request) */}
                  <button type="button" onClick={openAutoFillModal} disabled={autoFilling}
                    className="self-center px-3 py-1.5 bg-[#00E676]/10 hover:bg-[#00E676]/20 border border-[#00E676]/30 text-[#00E676] rounded-sm text-xs font-bold transition-all flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="bulk-auto-fill">
                    {autoFilling
                      ? <ArrowsClockwise size={13} className="animate-spin" />
                      : <Lightning size={13} weight="fill" />}
                    {autoFilling
                      ? (language === "ptBR" ? "Preenchendo..." : "Filling...")
                      : "Auto Fill"}
                  </button>
                  {/* Setup */}
                  <div className="flex items-end gap-1.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] text-white/40 uppercase tracking-wider">Setup</label>
                      <select value={bulkSetup} onChange={(e) => setBulkSetup(e.target.value)}
                        className="bg-[#050505] border border-white/10 rounded-sm px-2 py-1.5 text-xs text-white min-w-[130px]" data-testid="bulk-setup-select">
                        <option value="">{language === "ptBR" ? "Selecione..." : "Select..."}</option>
                        {setups.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    <button type="button" onClick={() => applyFieldToSelected("setup_id", bulkSetup)}
                      className="px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 rounded-sm text-[11px] font-bold transition-all" data-testid="bulk-apply-setup">
                      {language === "ptBR" ? "Aplicar" : "Apply"}
                    </button>
                  </div>
                  {/* Trade Style (mapeia para o timeframe interno) */}
                  <div className="flex items-end gap-1.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] text-white/40 uppercase tracking-wider">Trade Style</label>
                      <select value={bulkTimeframe} onChange={(e) => setBulkTimeframe(e.target.value)}
                        className="bg-[#050505] border border-white/10 rounded-sm px-2 py-1.5 text-xs text-white min-w-[110px]" data-testid="bulk-timeframe-select">
                        <option value="">{language === "ptBR" ? "Selecione..." : "Select..."}</option>
                        {TRADE_STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <button type="button" onClick={() => applyFieldToSelected("timeframe", bulkTimeframe)}
                      className="px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 rounded-sm text-[11px] font-bold transition-all" data-testid="bulk-apply-timeframe">
                      {language === "ptBR" ? "Aplicar" : "Apply"}
                    </button>
                  </div>
                  {/* Hand Size (Risk USD) */}
                  <div className="flex items-end gap-1.5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] text-white/40 uppercase tracking-wider">Hand Size (Risk USD)</label>
                      <input type="number" min="0" step="any" value={bulkHandSize} onChange={(e) => setBulkHandSize(e.target.value)}
                        placeholder="e.g. 100" className="bg-[#050505] border border-white/10 rounded-sm px-2 py-1.5 text-xs text-white w-[110px]" data-testid="bulk-handsize-input" />
                      {estimatedHandSize != null && (
                        <span className="text-[10px] text-[#00E676]/70" data-testid="bulk-handsize-estimate-hint">
                          {language === "ptBR" ? "Estimado pelo histórico" : "Estimated from history"}
                        </span>
                      )}
                    </div>
                    <button type="button" onClick={() => applyFieldToSelected("hand_size", bulkHandSize)}
                      className="px-2.5 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 rounded-sm text-[11px] font-bold transition-all" data-testid="bulk-apply-handsize">
                      {language === "ptBR" ? "Aplicar" : "Apply"}
                    </button>
                  </div>
                  <div className="flex-1" />
                  <button type="button" onClick={handleApplyAll}
                    className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white/80 rounded-sm text-xs font-bold transition-all flex items-center gap-1.5" data-testid="bulk-apply-all">
                    {language === "ptBR" ? "Aplicar tudo" : "Apply All"}
                  </button>
                  <button type="button" onClick={handleCompleteSelected} disabled={bulkCompleting}
                    className="px-4 py-2 bg-[#00E676] hover:bg-[#00c867] text-black rounded-sm text-xs font-bold transition-all disabled:opacity-50 flex items-center gap-1.5" data-testid="bulk-complete-selected">
                    <CheckCircle size={14} weight="bold" />
                    {bulkCompleting ? (language === "ptBR" ? "Concluindo..." : "Completing...") : (language === "ptBR" ? "Concluir selecionados" : "Complete Selected")}
                  </button>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-white/10 text-white/40 text-xs font-bold uppercase tracking-wider">
                      <th className="py-3 px-4 w-10">
                        <input type="checkbox"
                          checked={pendingTrades.length > 0 && pendingTrades.every((tr) => selectedIds.includes(tr.id))}
                          onChange={() => toggleSelectAll(pendingTrades.map((tr) => tr.id))}
                          className="accent-[#00E676] w-4 h-4 cursor-pointer" data-testid="bulk-select-all" />
                      </th>
                      <th className="py-3 px-4">{t.assetCol}</th>
                      <th className="py-3 px-4">{language === "ptBR" ? "Direção" : "Direction"}</th>
                      <th className="py-3 px-4">{language === "ptBR" ? "Data" : "Date"}</th>
                      <th className="py-3 px-4 text-right">P&L</th>
                      <th className="py-3 px-4">Setup</th>
                      <th className="py-3 px-4">Style</th>
                      <th className="py-3 px-4 text-right">Hand Size</th>
                      <th className="py-3 px-4 text-right">{language === "ptBR" ? "Status" : "Status"}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 text-sm">
                    {pendingTrades.map((tr) => {
                      const pnl = parseFloat(tr.realized_pnl || 0);
                      const d = draftValues[tr.id] || {};
                      const selected = selectedIds.includes(tr.id);
                      return (
                        <tr key={tr.id} className={`hover:bg-white/2 transition-colors ${selected ? "bg-[#00E676]/[0.03]" : ""}`} data-testid="recognized-trade-row">
                          <td className="py-3 px-4">
                            <input type="checkbox" checked={selected} onChange={() => toggleSelect(tr.id)}
                              className="accent-[#00E676] w-4 h-4 cursor-pointer" data-testid={`trade-select-${tr.id}`} />
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <div className="font-bold text-white">{tr.asset}</div>
                            <div className="text-[10px] font-semibold text-white/35 uppercase tracking-wide flex items-center gap-1 mt-0.5" data-testid="trade-origin">
                              {tr.exchange ? tr.exchange.toUpperCase() : "—"} <span className="text-white/20">|</span> {tr.source === "import" ? "Import" : "API"}
                            </div>
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className={`text-xs font-bold uppercase ${tr.direction === "long" ? "text-[#00E676]" : "text-red-400"}`}>{tr.direction}</span>
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-white/60">{formatDateTime(tr.closed_at)}</td>
                          <td className={`py-3 px-4 text-right font-mono font-bold ${pnl > 0 ? "text-[#00E676]" : pnl < 0 ? "text-red-400" : "text-white/50"}`}>
                            {pnl > 0 ? "+" : ""}{pnl.toFixed(2)}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-white/70" data-testid="trade-draft-setup">{d.setup_id ? setupName(d.setup_id) : <span className="text-white/25">—</span>}</td>
                          <td className="py-3 px-4 whitespace-nowrap text-white/70" data-testid="trade-draft-timeframe">{d.timeframe ? styleLabelForTf(d.timeframe) : <span className="text-white/25">—</span>}</td>
                          <td className="py-3 px-4 text-right whitespace-nowrap font-mono text-white/70" data-testid="trade-draft-handsize">{d.hand_size ? `$${parseFloat(d.hand_size).toFixed(0)}` : <span className="text-white/25">—</span>}</td>
                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            <button type="button" onClick={() => openCompleteModal(tr)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/15 border border-amber-500/30 text-amber-300 rounded-sm text-xs font-bold hover:bg-amber-500/25 transition-all"
                              data-testid="complete-trade-button">
                              <WarningCircle size={14} weight="fill" /> {language === "ptBR" ? "Completar Trade" : "Complete Trade"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Complete Trade modal (reduced: Setup, Timeframe, Hand Size only) */}
      <Dialog open={!!completeTrade} onOpenChange={(o) => { if (!o) setCompleteTrade(null); }}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-md" data-testid="complete-trade-modal">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wider text-sm">{language === "ptBR" ? "Completar Trade" : "Complete Trade"}</DialogTitle>
          </DialogHeader>
          {completeTrade && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm bg-white/2 border border-white/10 rounded-sm p-3">
                <div><span className="text-white/40 text-xs block">{t.assetCol}</span><span className="font-bold">{completeTrade.asset}</span></div>
                <div><span className="text-white/40 text-xs block">{language === "ptBR" ? "Direção" : "Direction"}</span><span className="font-bold uppercase">{completeTrade.direction}</span></div>
                <div><span className="text-white/40 text-xs block">Entry</span><span className="font-mono">{completeTrade.entry}</span></div>
                <div><span className="text-white/40 text-xs block">Exit</span><span className="font-mono">{completeTrade.closing_price}</span></div>
                <div className="col-span-2"><span className="text-white/40 text-xs block">{language === "ptBR" ? "Resultado" : "Result"}</span>
                  <span className={`font-mono font-bold ${parseFloat(completeTrade.realized_pnl) >= 0 ? "text-[#00E676]" : "text-red-400"}`}>
                    {parseFloat(completeTrade.realized_pnl) > 0 ? "+" : ""}{parseFloat(completeTrade.realized_pnl || 0).toFixed(2)} USDT
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-1">Setup *</label>
                <select value={cSetup} onChange={(e) => setCSetup(e.target.value)} className="w-full bg-[#050505] border border-white/10 rounded-sm px-3 py-2 text-sm text-white" data-testid="complete-setup-select">
                  <option value="">{language === "ptBR" ? "Selecione..." : "Select..."}</option>
                  {setups.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-1">Trade Style *</label>
                <select value={cTimeframe} onChange={(e) => setCTimeframe(e.target.value)} className="w-full bg-[#050505] border border-white/10 rounded-sm px-3 py-2 text-sm text-white" data-testid="complete-timeframe-select">
                  <option value="">{language === "ptBR" ? "Selecione..." : "Select..."}</option>
                  {TRADE_STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-1">Hand Size (USD) *</label>
                <input type="number" min="0" step="any" value={cHandSize} onChange={(e) => setCHandSize(e.target.value)} className="w-full bg-[#050505] border border-white/10 rounded-sm px-3 py-2 text-sm text-white" data-testid="complete-handsize-input" placeholder="e.g. 50" />
                {estimatedHandSize != null && (
                  <p className="text-[11px] text-[#00E676]/70 mt-1" data-testid="complete-handsize-estimate-hint">
                    {language === "ptBR" ? "Estimado pelo histórico de perdas" : "Estimated from historical losses"}
                  </p>
                )}
              </div>
              <button type="button" onClick={submitCompleteTrade} disabled={completing}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00E676] hover:bg-[#00c867] text-black rounded-sm text-sm font-bold transition-all disabled:opacity-50"
                data-testid="complete-trade-submit">
                {completing ? (language === "ptBR" ? "Salvando..." : "Saving...") : (language === "ptBR" ? "Completar e enviar ao Analytics" : "Complete & send to Analytics")}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Auto Fill: modal de seleção de Setup */}
      <Dialog open={showAutoFillModal} onOpenChange={(o) => { if (!o) setShowAutoFillModal(false); }}>
        <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-sm" data-testid="auto-fill-modal">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wider text-sm flex items-center gap-2">
              <Lightning size={15} weight="fill" className="text-[#00E676]" />
              Auto Fill
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <p className="text-xs text-white/50 leading-relaxed">
              {language === "ptBR"
                ? `Preencher ${selectedIds.length} trade(s) selecionado(s): Setup escolhido + Trade Style inferido por duração + Hand Size estimado.`
                : `Fill ${selectedIds.length} selected trade(s): chosen Setup + duration-inferred Trade Style + estimated Hand Size.`}
            </p>
            <div>
              <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-1.5">Setup *</label>
              <select
                value={autoFillSetup}
                onChange={(e) => setAutoFillSetup(e.target.value)}
                className="w-full bg-[#050505] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30"
                data-testid="auto-fill-setup-select"
                autoFocus
              >
                <option value="">{language === "ptBR" ? "Selecione um Setup..." : "Select a Setup..."}</option>
                {setups.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowAutoFillModal(false)}
                className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-sm text-sm font-semibold transition-all"
                data-testid="auto-fill-cancel">
                {language === "ptBR" ? "Cancelar" : "Cancel"}
              </button>
              <button
                type="button"
                onClick={executeAutoFill}
                disabled={!autoFillSetup}
                className="flex-1 px-4 py-2 bg-[#00E676] hover:bg-[#00c867] text-black rounded-sm text-sm font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                data-testid="auto-fill-confirm">
                <Lightning size={14} weight="fill" />
                {language === "ptBR" ? "Aplicar Auto Fill" : "Apply Auto Fill"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Exchange Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn" data-testid="request-exchange-modal">
          <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-8 w-full max-w-md relative">
            <button
              type="button"
              onClick={() => setShowRequestModal(false)}
              className="absolute top-5 right-5 text-white/40 hover:text-white transition-colors"
              data-testid="request-exchange-modal-close-button"
            >
              <XCircle size={22} />
            </button>

            <div className="flex items-center gap-3 mb-5">
              <ChatCenteredText size={26} weight="duotone" className="text-[#00E676]" />
              <h3 className="text-lg font-black text-white uppercase tracking-wider">{language === "ptBR" ? "Solicitar Integração" : "Request Integration"}</h3>
            </div>

            <p className="text-sm text-white/60 mb-6 leading-relaxed">
              {t.requestModalTitle}
            </p>

            <form onSubmit={handleRequestExchange} className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-white/40 uppercase tracking-wider mb-2">
                  {t.exchangeNameLabel}
                </label>
                <input
                  type="text"
                  value={requestedExchangeName}
                  onChange={(e) => setRequestedExchangeName(e.target.value)}
                  placeholder={t.requestNamePlaceholder}
                  className="w-full bg-[#050505] border border-white/10 rounded-sm px-4 py-3 text-sm text-white focus:outline-none focus:border-white/30 font-medium"
                  autoFocus
                  data-testid="request-exchange-name-input"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-sm text-sm font-semibold transition-all"
                >
                  {language === "ptBR" ? "Cancelar" : "Cancel"}
                </button>
                <button
                  type="submit"
                  disabled={submittingRequest}
                  className="flex-1 px-4 py-3 bg-[#00E676] hover:bg-[#00c867] text-black rounded-sm text-sm font-bold transition-all flex items-center justify-center gap-2"
                  data-testid="request-exchange-submit-button"
                >
                  {submittingRequest ? (
                    <ArrowsClockwise className="animate-spin" size={16} />
                  ) : (
                    <CheckCircle size={16} weight="bold" />
                  )}
                  {t.submitRequestBtn}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
